export interface OfficeHour {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

export interface DayHours {
  label: string;
  hours: string;
}

export interface CondensedHourRow {
  dayLabel: string;
  timeLabel: string;
}

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS: Record<string, string> = {
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
  sun: 'Sunday',
};

function hasHourValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const normalized = val.trim().toLowerCase();
  return (
    normalized !== '' &&
    normalized !== 'false' &&
    normalized !== 'null' &&
    normalized !== 'undefined' &&
    normalized !== '0' &&
    normalized !== 'closed'
  );
}

function formatOfficeTime(raw: string): string {
  const trimmed = raw.trim();
  if (/[ap]m/i.test(trimmed)) {
    return trimmed.replace(/([ap]m)/i, (m) => m.toLowerCase());
  }
  const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return trimmed;
  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? 'pm' : 'am';
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  return `${hours}:${minutes} ${period}`;
}

function normalizeDay(day: string): string {
  return day.trim().toLowerCase().slice(0, 3);
}

/**
 * Always emit all seven weekdays. Days missing from the source, or missing
 * open/close times (including a literal "closed" value), render as "Closed".
 */
export function listOfficeHours(raw: OfficeHour[]): DayHours[] {
  const byDay = new Map<string, OfficeHour>();
  const extras: OfficeHour[] = [];
  for (const entry of raw) {
    if (!hasHourValue(entry.day)) continue;
    const key = normalizeDay(entry.day);
    if (DAY_ORDER.includes(key)) {
      if (!byDay.has(key)) byDay.set(key, entry);
    } else {
      extras.push(entry);
    }
  }

  const rowFor = (label: string, entry: OfficeHour | undefined): DayHours => {
    const isClosed = !entry || !hasHourValue(entry.open_time) || !hasHourValue(entry.close_time);
    const hoursStr = isClosed
      ? 'Closed'
      : `${formatOfficeTime(entry!.open_time || '')} - ${formatOfficeTime(entry!.close_time || '')}`;
    return { label, hours: hoursStr };
  };

  const out: DayHours[] = DAY_ORDER.map((key) =>
    rowFor(DAY_LABELS[key], byDay.get(key)),
  );

  for (const entry of extras) {
    out.push(rowFor(entry.day || '', entry));
  }
  return out;
}

/**
 * Collapses consecutive days that share the same hours into a single range
 * row (e.g. Monday – Friday). Missing or closed days still appear, so a
 * weekday-only schedule becomes "Monday – Friday" + "Saturday – Sunday Closed".
 */
export function condenseOfficeHours(hours: OfficeHour[]): CondensedHourRow[] {
  if (hours.length === 0) return [];
  const days = listOfficeHours(hours);
  const rows: CondensedHourRow[] = [];
  let i = 0;
  while (i < days.length) {
    const cur = days[i];
    let j = i + 1;
    while (j < days.length && days[j].hours === cur.hours) {
      j++;
    }
    const span = j - i;
    const dayLabel =
      span === 1 ? cur.label : `${cur.label} – ${days[j - 1].label}`;
    rows.push({ dayLabel, timeLabel: cur.hours });
    i = j;
  }
  return rows;
}
